import React, { useEffect, useState } from "react";

import { useAngles, ENTRY_POINTS, AWARENESS_STAGES } from "../../../../services/useAngles";

/**
 * Workstation · Ads & Creative — **step 2, Angle Selection, in v2.**
 *
 * WAVE 3, SCREEN 2. The look is v2's; the behaviour is `services/useAngles`,
 * which v1's `AngleSelectionStep` also calls. One implementation, two
 * presentations.
 *
 * WHAT THE SWEEP FOUND AND FIXED BEFORE THIS PORT — all of it in the hook, so
 * v1 got every fix at the same moment:
 *
 *   · A stray `)}` rendered as literal text under the buttons. It had been
 *     there since the Create Custom form was removed on 2026-09-08: the closing
 *     token outlived its `{… && (`, parsed as JSXText, and React printed it.
 *     No test caught it because no test had ever rendered the component — all
 *     nine read the source as a string.
 *   · A failed read, a new session, and an offer with no angles were one blank
 *     grid with no words. Four states now, four messages.
 *   · Nothing was scoped: 456 angles live, 0 stamped with a session, one offer
 *     showing 83 at once.
 *   · Regeneration piled up repeats in silence — 11 of 26 offers hold duplicate
 *     angle names.
 *   · Four failure paths used `alert()`.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO: hide an angle. A repeated name is
 * announced, never dropped. Each angle is its own record, and a screen that
 * quietly bins rows it thinks are redundant is how work disappears.
 */
export default function AngleStep({ offer, sessionId, isNewSession,
                                    sessionStartTime, refreshKey,
                                    onComplete, onMultiAngleSelect,
                                    onTracksCreated }) {
  const A = useAngles({ offer, sessionId, isNewSession, sessionStartTime,
                        refreshKey, onComplete, onMultiAngleSelect,
                        onTracksCreated });

  const [openFor, setOpenFor] = useState(null);        // angle_id being refined
  const [refineText, setRefineText] = useState("");
  const [busyRefine, setBusyRefine] = useState(null);
  const [editFor, setEditFor] = useState(null);        // angle_id being edited
  const [draft, setDraft] = useState({});
  const [savingFor, setSavingFor] = useState(null);
  const [editError, setEditError] = useState(null);

  // Close any open drawer when the angle it belonged to leaves the screen —
  // otherwise a scoping change leaves a drawer editing something invisible.
  useEffect(() => {
    const ids = new Set(A.angles.map((a) => a.angle_id));
    if (openFor && !ids.has(openFor)) setOpenFor(null);
    if (editFor && !ids.has(editFor)) setEditFor(null);
  }, [A.angles, openFor, editFor]);

  const startEdit = (angle) => {
    setEditError(null);
    setEditFor(angle.angle_id);
    // Seeded from the angle so an untouched field round-trips unchanged
    // instead of being blanked by an empty form.
    setDraft({
      angle_name: angle.angle_name || "",
      core_belief_shift: angle.core_belief_shift || "",
      psychological_entry_point: angle.psychological_entry_point || "FRUSTRATION",
      target_awareness_stage: angle.target_awareness_stage || "PROBLEM_AWARE",
      target_persona: angle.target_persona || "",
      angle_rationale: angle.angle_rationale || "",
    });
  };

  const save = async (angleId) => {
    setSavingFor(angleId);
    setEditError(null);
    const res = await A.saveAngleEdits(angleId, draft);
    setSavingFor(null);
    if (res.ok) { setEditFor(null); setDraft({}); } else setEditError(res.error);
  };

  const refine = async (angleId) => {
    setBusyRefine(angleId);
    const ok = await A.refineAngle(angleId, refineText);
    setBusyRefine(null);
    if (ok) { setOpenFor(null); setRefineText(""); }
  };

  return (
    <div className="ws-step" data-testid="ws-angle-step">
      <div className="ws-step-head">
        <div>
          <h3 className="ws-step-title">Creative Angle</h3>
          <p className="dim">
            The strategic angle the creative argues from. <strong>Refine</strong>{" "}
            rewrites one from a note and keeps its psychology coherent — use it
            first. <strong>Edit text</strong> is for when you already know the
            words.
          </p>
        </div>
        <button
          type="button"
          className={A.angles.length === 0 ? "btn-primary btn-sm" : "btn-ghost btn-sm"}
          onClick={A.generateAngles}
          disabled={A.generating || A.loadState === "loading"}
          data-testid={A.angles.length === 0 ? "ws-angle-generate" : "ws-angle-regenerate"}
        >
          {A.generating
            ? (A.angles.length === 0 ? "Generating…" : "Regenerating…")
            : (A.angles.length === 0 ? "Generate Angles" : "Regenerate")}
        </button>
      </div>

      {/* FOUR STATES, FOUR MESSAGES (rule 10). They were one blank grid. */}
      {A.loadState === "loading" && A.angles.length === 0 ? (
        <div className="state ws-state" data-testid="ws-angle-loading">
          Reading this offer's angles…
        </div>
      ) : null}

      {A.loadState === "failed" ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-angle-load-failed">
          <p>The angles could not be read: {A.loadError}</p>
          <p className="dim">
            This is a failure, not an empty offer. Nothing has been lost.
          </p>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={A.retryLoad}
            data-testid="ws-angle-retry"
          >
            Try again
          </button>
        </div>
      ) : null}

      {A.loadState === "ok" && A.angles.length === 0 ? (
        <div className="state ws-state" data-testid="ws-angle-empty">
          {A.allCount > 0
            ? `This session has no angles yet. The offer holds ${A.allCount} from earlier work, not shown here.`
            : "This offer has no angles yet. Generate a set to begin."}
        </div>
      ) : null}

      {A.note ? (
        <p className="ws-step-note" role="status" data-testid="ws-angle-note">
          {A.note.text}
          {A.note.count > 1 ? (
            <span className="dim"> (asked {A.note.count} times)</span>
          ) : null}
        </p>
      ) : null}

      {A.actionError ? (
        <p className="ws-step-note state-error" role="alert" data-testid="ws-angle-error">
          {A.actionError}
        </p>
      ) : null}

      {A.angles.length > 0 ? (
        <div className="ws-angle-grid">
          {A.angles.map((angle) => {
            const picked = A.selectedAngles.includes(angle.angle_id);
            return (
              <article
                key={angle.angle_id}
                className={`ws-angle-card ${picked ? "is-picked" : ""}`}
                onClick={() => A.toggleAngle(angle.angle_id)}
                data-testid={`ws-angle-card-${angle.angle_id}`}
              >
                <header className="ws-angle-card-head">
                  <span
                    className={`ws-angle-tick ${picked ? "is-on" : ""}`}
                    aria-hidden="true"
                  >
                    {picked ? "✓" : ""}
                  </span>
                  <span className={`ws-angle-entry ep-${(angle.psychological_entry_point || "").toLowerCase()}`}>
                    {angle.psychological_entry_point}
                  </span>
                  <span className="dim">{angle.target_awareness_stage}</span>
                </header>

                <h4 className="ws-angle-name">{angle.angle_name}</h4>
                <p className="ws-angle-belief">{angle.core_belief_shift}</p>

                <div className="ws-angle-meta dim">
                  {angle.target_persona ? <span>{angle.target_persona}</span> : null}
                  {angle.angle_risk ? <span>{angle.angle_risk}</span> : null}
                  {angle.angle_memory_context ? (
                    <span className="ws-chip" title={angle.angle_memory_context}
                          data-testid={`ws-angle-intel-${angle.angle_id}`}>
                      Intelligence-backed
                    </span>
                  ) : null}
                  {angle.edited_at ? (
                    <span className="ws-chip" data-testid={`ws-angle-edited-${angle.angle_id}`}>
                      edited by hand
                    </span>
                  ) : null}
                  {angle.refined_at ? (
                    <span className="ws-chip" data-testid={`ws-angle-refined-${angle.angle_id}`}>
                      Refined
                    </span>
                  ) : null}
                  {/* Session provenance, said plainly. Nothing generated before
                      2026-09-09 carries one. */}
                  {!angle.session_id ? (
                    <span className="ws-chip is-quiet"
                          data-testid={`ws-angle-unstamped-${angle.angle_id}`}
                          title="Written before angles recorded their session">
                      session unknown
                    </span>
                  ) : null}
                </div>

                <div
                  className="ws-angle-actions"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`ws-angle-actions-${angle.angle_id}`}
                >
                  {/* "Approve" removed 2026-09-09 — see the tombstone in v1's
                      AngleSelectionStep. It committed one angle and advanced
                      while ignoring the checkboxes, which since MA-3 means
                      silently discarding the other tracks the operator asked
                      for. Tick and Continue is the one way through. */}
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => {
                      setOpenFor(openFor === angle.angle_id ? null : angle.angle_id);
                      setRefineText("");
                    }}
                    data-testid={`ws-angle-refine-toggle-${angle.angle_id}`}
                  >
                    {openFor === angle.angle_id ? "Cancel" : "Refine"}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => {
                      if (editFor === angle.angle_id) { setEditFor(null); setDraft({}); }
                      else startEdit(angle);
                    }}
                    data-testid={`ws-angle-edit-toggle-${angle.angle_id}`}
                  >
                    {editFor === angle.angle_id ? "Cancel" : "Edit text"}
                  </button>
                </div>

                {editFor === angle.angle_id ? (
                  <div
                    className="ws-angle-drawer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`ws-angle-edit-drawer-${angle.angle_id}`}
                  >
                    <p className="dim">
                      Editing this angle's text directly. Refine usually gives a
                      better-integrated result.
                    </p>
                    {editError ? (
                      <p className="state-error" role="alert"
                         data-testid={`ws-angle-edit-error-${angle.angle_id}`}>
                        {editError}
                      </p>
                    ) : null}

                    <label>
                      <span className="dim">Angle name</span>
                      <input
                        type="text"
                        value={draft.angle_name ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, angle_name: e.target.value }))}
                        data-testid={`ws-angle-edit-name-${angle.angle_id}`}
                      />
                    </label>

                    <label>
                      <span className="dim">Core belief shift</span>
                      <textarea
                        rows={3}
                        value={draft.core_belief_shift ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, core_belief_shift: e.target.value }))}
                        data-testid={`ws-angle-edit-belief-${angle.angle_id}`}
                      />
                    </label>

                    <div className="ws-angle-drawer-row">
                      <label>
                        <span className="dim">Entry point</span>
                        <select
                          value={draft.psychological_entry_point ?? "FRUSTRATION"}
                          onChange={(e) => setDraft((d) => ({ ...d, psychological_entry_point: e.target.value }))}
                          data-testid={`ws-angle-edit-entry-${angle.angle_id}`}
                        >
                          {ENTRY_POINTS.map((ep) => <option key={ep} value={ep}>{ep}</option>)}
                        </select>
                      </label>
                      <label>
                        <span className="dim">Awareness stage</span>
                        <select
                          value={draft.target_awareness_stage ?? "PROBLEM_AWARE"}
                          onChange={(e) => setDraft((d) => ({ ...d, target_awareness_stage: e.target.value }))}
                          data-testid={`ws-angle-edit-stage-${angle.angle_id}`}
                        >
                          {AWARENESS_STAGES.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </label>
                    </div>

                    <label>
                      <span className="dim">Target persona</span>
                      <input
                        type="text"
                        value={draft.target_persona ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, target_persona: e.target.value }))}
                        data-testid={`ws-angle-edit-persona-${angle.angle_id}`}
                      />
                    </label>

                    <label>
                      <span className="dim">Rationale</span>
                      <textarea
                        rows={2}
                        value={draft.angle_rationale ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, angle_rationale: e.target.value }))}
                        data-testid={`ws-angle-edit-rationale-${angle.angle_id}`}
                      />
                    </label>

                    <div className="ws-angle-drawer-foot">
                      <button type="button" className="btn-ghost btn-sm"
                              onClick={() => { setEditFor(null); setDraft({}); }}
                              data-testid={`ws-angle-edit-cancel-${angle.angle_id}`}>
                        Discard
                      </button>
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={() => save(angle.angle_id)}
                        disabled={
                          savingFor === angle.angle_id ||
                          !(draft.angle_name || "").trim() ||
                          !(draft.core_belief_shift || "").trim()
                        }
                        data-testid={`ws-angle-edit-save-${angle.angle_id}`}
                      >
                        {savingFor === angle.angle_id ? "Saving…" : "Save edits"}
                      </button>
                    </div>
                  </div>
                ) : null}

                {openFor === angle.angle_id ? (
                  <div
                    className="ws-angle-drawer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`ws-angle-refine-drawer-${angle.angle_id}`}
                  >
                    <label>
                      <span className="dim">What should change about this angle?</span>
                      <textarea
                        rows={2}
                        value={refineText}
                        onChange={(e) => setRefineText(e.target.value)}
                        placeholder="e.g. sharper belief shift, younger audience, less fear"
                        data-testid={`ws-angle-refine-input-${angle.angle_id}`}
                      />
                    </label>
                    <div className="ws-angle-drawer-foot">
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={() => refine(angle.angle_id)}
                        disabled={busyRefine === angle.angle_id || refineText.trim().length < 3}
                        data-testid={`ws-angle-refine-submit-${angle.angle_id}`}
                      >
                        {busyRefine === angle.angle_id ? "Refining…" : "Refine angle"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      {/* What is NOT on screen, and why. Same doctrine as every other step. */}
      {A.hiddenOtherSession || A.hiddenUnstamped ? (
        <p className="dim ws-step-note" data-testid="ws-angle-hidden-note">
          Not shown:{" "}
          {[
            A.hiddenOtherSession ? `${A.hiddenOtherSession} from other sessions on this offer` : null,
            A.hiddenUnstamped ? `${A.hiddenUnstamped} that predate session tracking (Cipher cannot tell which session made them)` : null,
          ].filter(Boolean).join("; ")}
          . This view is scoped to the current session.
        </p>
      ) : null}

      {A.predateNote > 0 ? (
        <p className="dim ws-step-note" data-testid="ws-angle-predate-note">
          Showing {A.predateNote} angle{A.predateNote > 1 ? "s" : ""} from this
          offer that predate session tracking — Cipher cannot tell which session
          made them. Angles generated from now on carry their session.
        </p>
      ) : null}

      {A.angles.length > 0 ? (
        <div className="ws-step-foot">
          <div className="ws-angle-regen">
            <textarea
              rows={2}
              value={A.feedback}
              onChange={(e) => A.setFeedback(e.target.value)}
              placeholder="What is wrong with these? The next generation will be told."
              data-testid="ws-angle-feedback"
            />
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={A.generateAngles}
              disabled={A.generating}
              data-testid="ws-angle-generate-more"
            >
              {A.generating ? "Generating…" : A.feedback ? "Regenerate with feedback" : "Generate more"}
            </button>
          </div>

          {/* MA-3: this was a warning that the extra angles lived in memory
              and would be lost on reload. They are real sessions now. */}
          {A.multiAngleWarning ? (
            <p className="ws-step-note" data-testid="ws-angle-multi-warning">
              Continuing will open {A.selectedAngles.length} creative sessions —
              one per angle. Each runs its own hooks, scripts, CTA and prompts,
              and can be finished and submitted on its own.
            </p>
          ) : null}

          <div className="ws-angle-confirm">
            <span className="dim">
              {A.selectedAngles.length === 0
                ? "Select one or more angles to continue"
                : `${A.selectedAngles.length} angle${A.selectedAngles.length > 1 ? "s" : ""} selected`}
            </span>
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => A.proceed()}
              disabled={A.selecting || A.selectedAngles.length === 0}
              data-testid="ws-angle-confirm"
            >
              {A.selecting
                ? "Selecting…"
                : `Continue with ${A.selectedAngles.length} angle${A.selectedAngles.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
